import { useCallback, useState } from 'react'

import { IconCog, TextButton, Tooltip } from '@smartbear/swagger-portal-component-library'
import { useTranslation } from 'react-i18next'
import { useHydrated } from 'remix-utils/use-hydrated'

import { getRoutingType } from '~/portal/components/ApiViewer/RoutingSettingsButton/helpers'
import { RoutingSettingsModal } from '~/portal/components/ApiViewer/RoutingSettingsButton/RoutingSettingsModal'
import { usePersonalizedAuth } from '~/portal/hooks/usePersonalizedAuth'
import { useRoutingSettings } from '~/portal/hooks/useRoutingSettings'
import { RoutingOption, RoutingSettings } from '~/shared/types/portal'

export const RoutingSettingsButton = ({
  routingSettings,
}: {
  routingSettings?: RoutingSettings,
}) => {
  const { t } = useTranslation()
  const personalizedAuthActive = usePersonalizedAuth()

  // button label contains value that comes from local storage so we need to render
  // it after hydration in order to prevent hydration error
  const isHydrated = useHydrated()

  const [{ routing, credentialsEnabled }, setRoutingSettings] = useRoutingSettings(routingSettings)

  const [open, setOpen] = useState(false)
  const [routingType, setRoutingType] = useState(getRoutingType(routing, credentialsEnabled))

  const toggleModal = () => setOpen(!open)

  const onModalConfirm = useCallback((routing: RoutingOption, credentialsEnabled: boolean) => {
    setRoutingType(getRoutingType(routing, credentialsEnabled))
    setRoutingSettings({
      routing,
      credentialsEnabled,
    })
    setOpen(false)
  }, [])

  // routing is forced through the proxy so the STS token never reaches the browser
  // The hover colour is a bespoke #3A4452 from the Figma sample portal until design confirms the token; --sp-routing-notice-hover overrides it
  if (personalizedAuthActive) {
    return (
      <Tooltip
        content={t('components.ui.apiViewer.routing.forcedTooltip')}
        trigger="hover"
        defaultComp={
          <span
            data-testid="routing-forced-notice"
            tabIndex={0}
            className="sp-routing-forced-notice inline-flex items-center h-8 px-3 font-medium rounded cursor-default text-btn-small text-neutral-80 dark:text-neutral-20 duration-200 transition-color hover:bg-(--sp-routing-notice-hover,#3A4452) hover:text-(--sp-color-text-inverse) focus:bg-(--sp-routing-notice-hover,#3A4452) focus:text-(--sp-color-text-inverse) dark:hover:bg-(--sp-color-surface-inverse) dark:focus:bg-(--sp-color-surface-inverse) focus:outline-none"
          >
            {t('components.ui.apiViewer.routing.forcedNotice')}
          </span>
        }
        placement="right"
      />
    )
  }

  return (
    <>
      <Tooltip
        content={t('components.ui.apiViewer.routing.button.tooltip')}
        trigger="hover"
        defaultComp={
          <TextButton
            data-testid="routing-button"
            size="s"
            icon={<IconCog width={12} height={12} />}
            className="sp-routing-settings-button ml-0 duration-200 transition-color hover:bg-(--sp-color-surface-inverse) dark:hover:bg-(--sp-color-surface-inverse) hover:text-(--sp-color-text-inverse) dark:hover:text-(--sp-color-text-inverse)"
            onClick={toggleModal}
            loading={!isHydrated}
          >
            {t('components.ui.apiViewer.routing.button.label')}
            {isHydrated ? (
              <>: {t(`components.ui.apiViewer.routing.button.types.${routingType}`)}</>
            ) : null}
          </TextButton>
        }
        placement="right"
      />
      {open ? (
        <RoutingSettingsModal
          isOpen={open}
          initialRouting={routing}
          initialCredentialsEnabled={credentialsEnabled}
          onConfirm={onModalConfirm}
          onClose={toggleModal}
        />
      ) : null}
    </>
  )
}